import { Link as LinkIcon } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { fetchUrlMetadata, UrlMetadata } from 'util/fetchUrlMetadata';

interface EntityThumbnailProps {
    url: string;
    urlType: 'img' | 'page';
    isProfilePage: boolean;
    onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    referenceLink?: string;
    onHeightChange?: (height: number) => void;
    maxHeight?: number;
}

const EntityThumbnail: React.FC<EntityThumbnailProps> = ({ 
    url, 
    urlType, 
    onCardClick,
    onHeightChange,
    maxHeight
}) => {
    const [metadata, setMetadata] = useState<UrlMetadata | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        const getMetadata = async () => {
            if (urlType === 'page') {
                const data = await fetchUrlMetadata(url);
                setMetadata(data);
            }
        };

        getMetadata();
    }, [url, urlType]);

    useEffect(() => {
        const image = imageRef.current;
        if (!image) return;

        const handleLoad = () => {
            if (onHeightChange) {
                onHeightChange(image.clientHeight);
            }
        };

        image.addEventListener('load', handleLoad);
        return () => image.removeEventListener('load', handleLoad);
    }, [onHeightChange]);

    // Create thumbnail class with conditional dark background for SVG images
    const getThumbnailClass = (imageUrl: string) => {
        const isSvg = imageUrl.toLowerCase().includes('.svg');
        const darkBgClass = isSvg ? '' : '';
        return `bg-transparent ${darkBgClass} max-h-[50vh] h-full object-cover object-center md:object-center w-full`;
    };

    if (urlType === 'img') {
        if (!url) return (
            <div
              role="button"
              tabIndex={0}
              className="no-metadata-link flex w-full items-center justify-center rounded-md bg-transparent md:h-[126px] md:w-[240px]"
              onClick={onCardClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
              aria-label="Open link preview"
            >
              <LinkIcon className="no-metadata-link size-12 text-eoe-text-2 dark:text-eoe-dark-text-2" />
            </div>
        );

        return (
            <img
                src={url}
                alt="Entity Thumbnail"
                className={getThumbnailClass(url)}
                ref={imageRef}
                onClick={onCardClick}
            />
        );
    }

    if (urlType === 'page') {
        if (!metadata?.image) return (
        <div
          role="button"
          tabIndex={0}
          className="no-metadata-link flex h-full max-h-[50vh] w-full items-center justify-center rounded-md border border-eoe-icon dark:border-eoe-dark-icon md:h-[126px] md:w-[240px]"
          onClick={onCardClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
          aria-label="Open link preview"
        >
          <LinkIcon className="no-metadata-link size-12 text-eoe-text-2 dark:text-eoe-dark-text-2" />
        </div>
        );

        const imageUrl = metadata.image[0]?.url || metadata.image.url;
        const imageAlt = metadata.title || metadata.image[0]?.alt || 'Link preview';
        
        return (
            <div className="rounded-md flex object-scale-down bg-transparent justify-center items-center">
                <img
                    src={imageUrl}
                    alt={imageAlt}
                    className={getThumbnailClass(imageUrl)}
                    ref={imageRef}
                    onClick={onCardClick}
                />
            </div>
        );
    }

    return null;
};

export default EntityThumbnail;